import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api, money } from "@/lib/api";
import { Heart, Star } from "lucide-react";

export const SupporterWall = ({ donateUrl }) => {
  const [supporters, setSupporters] = useState([]);
  useEffect(() => { api.get("/supporters").then(({ data }) => setSupporters(data)).catch(() => {}); }, []);

  const featured = supporters.find((s) => s.featured);
  const rest = supporters.filter((s) => s.id !== featured?.id);

  return (
    <section className="max-w-5xl mx-auto px-6 lg:px-10 py-16" data-testid="supporter-wall">
      <div className="text-center max-w-2xl mx-auto mb-10">
        <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#C1272D] mb-3">With gratitude</p>
        <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight text-[#1F4D2E]">Our supporters wall.</h2>
        <p className="text-[#4F6B54] mt-4 leading-relaxed">Every name here is a real gift that brought a child closer to safety. Thank you for standing with us.</p>
      </div>

      {supporters.length === 0 ? (
        <div className="border border-dashed border-[#1F4D2E]/25 rounded-xl py-14 px-6 text-center bg-[#FBF8F0]" data-testid="supporter-wall-empty">
          <span className="w-14 h-14 rounded-full bg-[#C1272D]/10 text-[#C1272D] flex items-center justify-center mx-auto mb-5"><Heart size={24} /></span>
          <p className="font-display text-2xl sm:text-3xl font-semibold text-[#1F4D2E] leading-snug max-w-xl mx-auto mb-3">“No child hungry. No child homeless. No child left behind.”</p>
          <p className="text-[#4F6B54] max-w-md mx-auto mb-8">This wall is waiting for its first name. Be the very first supporter of Refugium Ethiopia — your gift starts everything.</p>
          {donateUrl && (
            <a href={donateUrl} target="_blank" rel="noopener noreferrer" data-testid="supporter-wall-donate"
              className="inline-flex items-center gap-2 bg-[#C1272D] text-white rounded-full px-7 py-3 font-semibold hover:bg-[#9E1F24] transition-colors">
              <Heart size={17} fill="white" /> Become our first supporter
            </a>
          )}
        </div>
      ) : (
        <>
          {featured && (
            <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
              className="relative bg-[#1F4D2E] text-white rounded-xl p-8 sm:p-10 mb-8 overflow-hidden shadow-[0_20px_50px_rgb(0,0,0,0.15)]" data-testid="featured-supporter">
              <span className="absolute top-0 right-0 w-40 h-40 bg-[#E0A526]/15 rounded-full -mr-16 -mt-16" />
              <div className="relative">
                <p className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-[#E0A526] mb-4">
                  <Star size={15} fill="#E0A526" /> Supporter of the Month
                </p>
                <div className="flex flex-wrap items-center gap-4 mb-3">
                  <h3 className="font-display text-3xl sm:text-4xl font-semibold">{featured.name}</h3>
                  {featured.amount != null && <span className="bg-[#E0A526] text-[#1F4D2E] text-sm font-bold px-3 py-1 rounded-full">{money(featured.amount)}</span>}
                </div>
                {featured.message && <p className="text-lg text-[#E7EDE2]/90 italic leading-relaxed max-w-2xl">“{featured.message}”</p>}
              </div>
            </motion.div>
          )}
          {rest.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="supporter-list">
              {rest.map((s, i) => (
                <motion.div key={s.id} initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="bg-white border border-[#1F4D2E]/10 rounded-lg p-5" data-testid={`supporter-${s.id}`}>
                  <div className="flex items-start justify-between gap-3 mb-1">
                    <h3 className="font-display text-xl font-semibold text-[#1F4D2E]">{s.name}</h3>
                    {s.amount != null && <span className="shrink-0 bg-[#16A34A]/10 text-[#16A34A] text-xs font-bold px-2.5 py-1 rounded-full">{money(s.amount)}</span>}
                  </div>
                  {s.message && <p className="text-sm text-[#4F6B54] italic leading-relaxed">“{s.message}”</p>}
                </motion.div>
              ))}
            </div>
          )}
        </>
      )}
    </section>
  );
};

export default SupporterWall;
